import { useEffect, useState } from "react";

/**
 * Minimal reader for the legal markdown documents under public/legal/.
 *
 * The files are the single tracked copy from design/assets/lyra_legal and are
 * copied in by scripts/copy-legal-assets.js before every start/build. Their
 * structure is produced by .docs/privacy-and-legal/docx_to_md.py, so it is
 * fixed and small: "#", "##", "###", "- " and underscore dividers. The block
 * types mirror the Flutter parser in
 * design/lib/shared/legal/data/models/legal_document.dart so both apps render
 * the same document the same way.
 */
export type LegalBlockType =
  | "documentTitle"
  | "section"
  | "subSection"
  | "bullet"
  | "divider"
  | "paragraph";

export interface LegalBlock {
  type: LegalBlockType;
  text: string;
}

export const parseLegalDocument = (raw: string): LegalBlock[] => {
  const blocks: LegalBlock[] = [];

  raw.split(/\r?\n/).forEach((rawLine) => {
    const line = rawLine.trim();

    if (line.length === 0) {
      return;
    }

    if (/^_{3,}$/.test(line)) {
      blocks.push({ type: "divider", text: "" });
      return;
    }

    if (line.startsWith("### ")) {
      blocks.push({ type: "subSection", text: line.slice(4).trim() });
      return;
    }

    if (line.startsWith("## ")) {
      blocks.push({ type: "section", text: line.slice(3).trim() });
      return;
    }

    if (line.startsWith("# ")) {
      blocks.push({ type: "documentTitle", text: line.slice(2).trim() });
      return;
    }

    if (line.startsWith("- ")) {
      blocks.push({ type: "bullet", text: line.slice(2).trim() });
      return;
    }

    blocks.push({ type: "paragraph", text: line });
  });

  return blocks;
};

/**
 * Splits a parsed document at a `###` heading. Used to show the withdrawal
 * instructions and the model withdrawal form as two separate panels, both of
 * which have to be visible before the order is placed.
 */
export const splitAtSubSection = (
  blocks: LegalBlock[],
  heading: string,
): { before: LegalBlock[]; from: LegalBlock[] } => {
  const index = blocks.findIndex(
    (block) => block.type === "subSection" && block.text === heading,
  );

  if (index < 0) {
    return { before: blocks, from: [] };
  }

  return { before: blocks.slice(0, index), from: blocks.slice(index) };
};

export type LegalDocumentStatus = "loading" | "ready" | "error";

export interface LegalDocumentState {
  blocks: LegalBlock[];
  status: LegalDocumentStatus;
}

/** Loads and parses one of the documents in public/legal/. */
export const useLegalDocument = (fileName: string): LegalDocumentState => {
  const [state, setState] = useState<LegalDocumentState>({
    blocks: [],
    status: "loading",
  });

  useEffect(() => {
    let cancelled = false;
    setState({ blocks: [], status: "loading" });

    const url = `${process.env.PUBLIC_URL || ""}/legal/${fileName}`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load ${fileName}: ${response.status}`);
        }
        // nginx serves this SPA with `try_files ... /index.html`, so a renamed or
        // missing document answers 200 with the app shell instead of 404. Taking that
        // as a legal text would parse HTML into paragraphs, report the withdrawal
        // instructions as present and re-enable the order button - a fail-open hole in
        // a page that is deliberately fail-closed. So the content type is checked, and
        // the parsed result has to actually look like a document.
        const contentType = response.headers.get("content-type") ?? "";
        if (contentType.includes("text/html")) {
          throw new Error(`Expected markdown for ${fileName}, got ${contentType}`);
        }

        return response.text();
      })
      .then((raw) => {
        if (cancelled) {
          return;
        }
        if (raw.trimStart().startsWith("<")) {
          throw new Error(`Expected markdown for ${fileName}, got markup`);
        }

        const blocks = parseLegalDocument(raw);

        if (blocks.length === 0) {
          throw new Error(`Legal document ${fileName} is empty`);
        }

        setState({ blocks, status: "ready" });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setState({ blocks: [], status: "error" });
      });

    return () => {
      cancelled = true;
    };
  }, [fileName]);

  return state;
};
